/**
 * Holiday calendar (Shared area). Bill 2026-10-16: Retail owns the bank's one holiday calendar
 * (admin-portal-shape §9.3); pcb-loans-api and the others read it through GET v3/retail/holidays.
 *
 *   /shared/holidays          list (GET v3/retail/admin/holidays)
 *   /shared/holidays/new      add      (POST)
 *   /shared/holidays/:id      edit / deactivate / reactivate (PUT, DELETE)
 *
 * Pages, not pop-ups. Every write sends a reason (x-pcb-change-reason) and is recorded in the
 * Retail change history with who, when, before and after. Every date cites where it came from.
 * Who may change it: the shared.holidays surface roles (default Admin; Global Admin always).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable } from '@/components/DataTable';
import { BoolField, SelectField, TextField } from '@/components/FormField';
import { ReasonField, reasonProblem, retailReasonHeader } from '@/components/ChangeReason';

interface Holiday {
  holidayId: number;
  date: string;
  name: string;
  kind: 'federal' | 'bank' | string;
  isActive: boolean;
  source: string;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

interface AdminListResponse {
  success: boolean;
  canWrite: boolean;
  writeRoles: string[];
  holidays: Holiday[];
}

interface WriteResponse {
  success: boolean;
  holiday?: Holiday;
  before?: Holiday | null;
  after?: Holiday | null;
  error?: string;
  code?: string;
}

const ROUTE = '/v3/retail/admin/holidays';
const BASE = '/shared/holidays';
const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function weekday(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '';
  return WEEKDAY[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? '';
}

function errorText(e: unknown): string {
  if (ApiError.isApiError(e)) return e.code ? `${e.message} (${e.code})` : e.message;
  return e instanceof Error ? e.message : String(e);
}

function useHolidays(includeInactive: boolean): { data: AdminListResponse | null; error: string | null; reload: () => void } {
  const client = useApiClient();
  const [data, setData] = useState<AdminListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    client.http
      .get<AdminListResponse>(`${ROUTE}?includeInactive=${includeInactive ? 'true' : 'false'}`)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(errorText(e));
      });
    return () => {
      cancelled = true;
    };
  }, [client, includeInactive, nonce]);
  return { data, error, reload: () => setNonce((n) => n + 1) };
}

export function HolidaysListPage(): JSX.Element {
  const [showInactive, setShowInactive] = useState(false);
  const { data, error } = useHolidays(true);
  const rows = useMemo(() => (data?.holidays ?? []).filter((h) => showInactive || h.isActive), [data, showInactive]);
  const years = useMemo(
    () => [...new Set((data?.holidays ?? []).filter((h) => h.isActive).map((h) => h.date.slice(0, 4)))].sort(),
    [data],
  );
  const thisYear = String(new Date().getFullYear());
  const nextYear = String(new Date().getFullYear() + 1);

  return (
    <div>
      <h2>Holiday calendar</h2>
      <p className="page-intro">
        The bank&rsquo;s one list of holidays. Retail funding deadlines and SLA targets, Loans rescission and service-desk
        dates read it from here; no platform keeps its own copy. A date is listed once, as a federal holiday or a bank
        closure, with where it came from. Weekend rules stay with each platform.
      </p>
      {error && <Banner tone="err">{error}</Banner>}
      {!data && !error && <Spinner />}
      {data && (
        <>
          {(!years.includes(thisYear) || !years.includes(nextYear)) && (
            <Banner tone="warn">
              The calendar has no holidays for {[thisYear, nextYear].filter((y) => !years.includes(y)).join(' or ')}. Business-day
              counts for those years will skip weekends only.
            </Banner>
          )}
          <div className="toolbar">
            <BoolField label="Show deactivated" value={showInactive} onChange={setShowInactive} />
            <span className="toolbar__spacer" />
            {data.canWrite ? (
              <Link className="btn btn--primary" to={`${BASE}/new`}>
                Add a holiday
              </Link>
            ) : (
              <span className="form-field__hint">
                Read-only for you. Changes need one of: {[...data.writeRoles, 'SuperAdmin'].join(', ')}.
              </span>
            )}
          </div>
          <DataTable
            rows={rows}
            rowKey={(h) => String(h.holidayId)}
            initialSort={{ columnIndex: 0, dir: 'asc' }}
            emptyState={<div className="empty">No holidays on the calendar yet.</div>}
            columns={[
              { header: 'Date', sortKey: (h) => h.date, cell: (h) => <Link to={`${BASE}/${h.holidayId}`}>{h.date}</Link> },
              { header: 'Day', cell: (h) => weekday(h.date) },
              { header: 'Holiday', sortKey: (h) => h.name, cell: (h) => h.name },
              { header: 'Kind', cell: (h) => <Pill tone={h.kind === 'bank' ? 'info' : 'muted'}>{h.kind}</Pill> },
              { header: 'Status', cell: (h) => (h.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">deactivated</Pill>) },
              { header: 'Source', cell: (h) => <span className="value-clip">{h.source}</span> },
            ]}
          />
          <p className="form-field__hint">
            Every change is in <Link to="/retail/history?table=BankHolidays">Change history</Link>.
          </p>
        </>
      )}
    </div>
  );
}

interface FormState {
  date: string;
  name: string;
  kind: string;
  source: string;
}

const EMPTY: FormState = { date: '', name: '', kind: 'federal', source: '' };

function formProblem(f: FormState): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.date)) return 'Pick the date.';
  if (!f.name.trim()) return 'Name the holiday.';
  if (!f.source.trim()) return 'Say where this date comes from (a schedule, memo, or who decided and when).';
  return null;
}

export function HolidayEditPage(): JSX.Element {
  const { id } = useParams();
  const isNew = id === 'new';
  const holidayId = isNew ? null : Number(id);
  const client = useApiClient();
  const navigate = useNavigate();
  const { data, error, reload } = useHolidays(true);
  const existing = useMemo(() => data?.holidays.find((h) => h.holidayId === holidayId) ?? null, [data, holidayId]);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    if (existing) setForm({ date: existing.date, name: existing.name, kind: existing.kind, source: existing.source });
  }, [existing]);

  const write = useCallback(
    async (kind: 'save' | 'deactivate' | 'reactivate') => {
      setTried(true);
      setSaveError(null);
      setDone(null);
      if (reasonProblem(reason)) return;
      if (kind === 'save' && formProblem(form)) return;
      setSaving(true);
      try {
        const headers = retailReasonHeader(reason);
        let r: WriteResponse;
        if (kind === 'deactivate') {
          r = await client.http.del<WriteResponse>(`${ROUTE}/${holidayId}`, { headers });
        } else if (kind === 'reactivate') {
          r = await client.http.put<WriteResponse>(`${ROUTE}/${holidayId}`, { headers, body: { isActive: true } });
        } else if (isNew) {
          r = await client.http.post<WriteResponse>(ROUTE, { headers, body: { ...form, isActive: true } });
        } else {
          r = await client.http.put<WriteResponse>(`${ROUTE}/${holidayId}`, { headers, body: form });
        }
        if (!r.success) throw new Error(r.error ?? 'The change was not saved.');
        if (isNew && r.holiday) {
          navigate(`${BASE}/${r.holiday.holidayId}`, { replace: true });
          return;
        }
        setReason('');
        setTried(false);
        setDone(kind === 'deactivate' ? 'Deactivated.' : kind === 'reactivate' ? 'Reactivated.' : 'Saved.');
        reload();
      } catch (e) {
        setSaveError(errorText(e));
      } finally {
        setSaving(false);
      }
    },
    [client, form, holidayId, isNew, navigate, reason, reload],
  );

  const back = <Link to={BASE}>Back to the holiday calendar</Link>;
  if (error) return <div>{back}<Banner tone="err">{error}</Banner></div>;
  if (!data) return <Spinner />;
  if (!isNew && !existing) return <div>{back}<div className="empty">Holiday #{id} is not on the calendar.</div></div>;
  if (!data.canWrite) {
    return (
      <div>
        <p>{back}</p>
        <Banner tone="info">You can read the calendar but not change it.</Banner>
      </div>
    );
  }

  const problem = tried ? formProblem(form) : null;
  const set = (k: keyof FormState) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <p>{back}</p>
      <h2>{isNew ? 'Add a holiday' : `${existing!.date} ${existing!.name}`}</h2>
      {existing && (
        <p className="form-field__hint">
          {existing.isActive ? 'Active.' : 'Deactivated.'} Added {new Date(existing.createdAt).toLocaleString()}
          {existing.createdBy ? ` by ${existing.createdBy}` : ''}; last changed {new Date(existing.updatedAt).toLocaleString()}
          {existing.updatedBy ? ` by ${existing.updatedBy}` : ''}.
        </p>
      )}
      {done && <Banner tone="info">{done}</Banner>}
      <Card title={isNew ? 'The holiday' : 'Edit'}>
        <label className="form-field">
          <span className="form-field__label">
            Date<span className="form-field__required" aria-hidden> *</span>
          </span>
          <input type="date" className="form-input" value={form.date} onChange={(e) => set('date')(e.target.value)} />
          {form.date && <span className="form-field__hint">{weekday(form.date)}</span>}
        </label>
        <TextField label="Name" required value={form.name} onChange={set('name')} maxLength={128} placeholder="Thanksgiving Day" />
        <SelectField
          label="Kind"
          value={form.kind}
          onChange={set('kind')}
          options={[
            { value: 'federal', label: 'Federal holiday' },
            { value: 'bank', label: 'Bank closure (not a federal holiday)' },
          ]}
        />
        <TextField
          label="Source"
          required
          value={form.source}
          onChange={set('source')}
          maxLength={512}
          hint="Where this date comes from: the Federal Reserve or OPM schedule, a board or Executive Management memo, or who decided and when."
        />
        {problem && <Banner tone="err">{problem}</Banner>}
        <ReasonField value={reason} onChange={setReason} showError={tried} />
        {saveError && <Banner tone="err">{saveError}</Banner>}
        <div className="card-actions-row">
          <Button onClick={() => void write('save')} disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Add holiday' : 'Save changes'}
          </Button>
          {existing && existing.isActive && (
            <Button variant="danger" onClick={() => void write('deactivate')} disabled={saving}>
              Deactivate
            </Button>
          )}
          {existing && !existing.isActive && (
            <Button variant="ghost" onClick={() => void write('reactivate')} disabled={saving}>
              Reactivate
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
